/**
 * MobileTrainingRecordsList — Training Records mobile card list ( <lg ).
 * Batch 6.2 — pure presentation swap for the desktop 9-column
 * `<table data-testid="training-records-table">`. Uses the SAME
 * `/training/records` payload the desktop tab uses, the SAME filter
 * state contract (site_id / category / status / expiry_period + search),
 * and the SAME manager Edit / Delete handlers.
 *
 * No new APIs. No client-side derivation of status / expiry —
 * `status_computed` is authoritative from the server.
 * Care workers do NOT see Edit / Delete (canManage=false → no action row).
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Pencil, Trash2, FileText, Search,
  CheckCircle2, Clock, AlertOctagon, Minus, ChevronRight,
} from "lucide-react";
import TrainingRecordDialog from "@/components/training/TrainingRecordDialog";
import { MobileFilterDrawer } from "@/components/mobile";

const STATUSES = [
  { v: "compliant",     label: "Compliant" },
  { v: "expiring",      label: "Expiring Soon" },
  { v: "expired",       label: "Expired" },
  { v: "not_completed", label: "Not Completed" },
  { v: "in_progress",   label: "In Progress" },
];

const EXPIRY_PERIODS = [
  { v: "expired", label: "Already expired" },
  { v: "30d",     label: "Next 30 days" },
  { v: "60d",     label: "Next 60 days" },
  { v: "90d",     label: "Next 90 days" },
];

// Status meta: colour + Icon + text label (never colour alone)
const STATUS_META = {
  compliant:     { label: "Compliant",     Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  expiring:      { label: "Expiring",      Icon: Clock,        cls: "bg-amber-100 text-amber-900 border-amber-300" },
  expired:       { label: "Expired",       Icon: AlertOctagon, cls: "bg-red-100 text-red-800 border-red-300" },
  not_completed: { label: "Not Completed", Icon: Minus,        cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress:   { label: "In Progress",   Icon: Clock,        cls: "bg-sky-100 text-sky-800 border-sky-300" },
};

function humanise(v) { return v ? String(v).replace(/_/g, " ") : "—"; }
function fmtDate(v) { return v ? String(v).slice(0, 10) : "—"; }

// Days delta between today and given YYYY-MM-DD (presentation only).
// The AUTHORITATIVE compliance state is `status_computed` from the server;
// this helper just formats a friendlier "in X days" / "X days ago" hint
// alongside expiring / expired records so mobile users see urgency at a glance.
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function StatusPill({ status, testid }) {
  const s = STATUS_META[status] || STATUS_META.not_completed;
  const Icon = s.Icon;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.cls}`}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function MobileTrainingRecordsList({
  onOpenStaff, canManage, refreshTick, onChanged,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ site_id: "", category: "", status: "", expiry_period: "" });
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (search && search.trim()) params.search = search.trim();
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const r = await api.get("/training/records", { params });
      setRows(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters, search]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
    api.get("/training/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this training record? This cannot be undone.")) return;
    try {
      await api.delete(`/training/${id}`);
      toast.success("Record deleted");
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activeFilterCount =
    (filters.site_id ? 1 : 0) + (filters.category ? 1 : 0) + (filters.status ? 1 : 0) + (filters.expiry_period ? 1 : 0);

  return (
    <div className="space-y-3" data-testid="mobile-training-records">
      {/* Search + Filters row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff or course…"
            data-testid="mobile-training-search"
            className="w-full h-11 pl-8 pr-3 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>
        <MobileFilterDrawer
          triggerLabel="Filters"
          triggerClassName="shrink-0"
          activeCount={activeFilterCount}
          onClear={() => setFilters({ site_id: "", category: "", status: "", expiry_period: "" })}
          testid="mobile-training-filter-drawer"
        >
          <div className="space-y-3">
            <FS label="Site" value={filters.site_id} onChange={(v) => setFilters({ ...filters, site_id: v })}
              options={sites.map((s) => ({ v: s.id, label: s.name }))} testid="mobile-filter-training-site" />
            <FS label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })}
              options={categories.map((c) => ({ v: c, label: c }))} testid="mobile-filter-training-category" />
            <FS label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
              options={STATUSES.map((s) => ({ v: s.v, label: s.label }))} testid="mobile-filter-training-status" />
            <FS label="Expiry period" value={filters.expiry_period} onChange={(v) => setFilters({ ...filters, expiry_period: v })}
              options={EXPIRY_PERIODS.map((e) => ({ v: e.v, label: e.label }))} testid="mobile-filter-training-expiry" />
          </div>
        </MobileFilterDrawer>
      </div>

      <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-training-count">
        {rows.length} record{rows.length === 1 ? "" : "s"}
      </div>

      {/* Card list */}
      {loading ? (
        <div className="text-sm text-slate-500 p-4" data-testid="mobile-training-loading">Loading records…</div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center"
          data-testid="mobile-training-empty"
        >
          <div className="text-sm font-semibold text-slate-700">No training records match the current filters.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mobile-training-list">
          {rows.map((r) => {
            const status = r.status_computed;
            const days = daysUntil(r.expiry_date);
            const expiryLine = r.expiry_date ? (
              status === "expired" && days != null
                ? `Expired ${fmtDate(r.expiry_date)} · ${Math.abs(days)}d ago`
                : status === "expiring" && days != null
                  ? `Expires ${fmtDate(r.expiry_date)} · in ${Math.max(0, days)}d`
                  : `Expires ${fmtDate(r.expiry_date)}`
            ) : "No expiry recorded";
            return (
              <li
                key={r.id}
                data-testid={`mobile-training-row-${r.id}`}
                className="rounded-lg border border-slate-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => r.resolved_staff_user_id && onOpenStaff?.(r.resolved_staff_user_id)}
                  data-testid={`mobile-training-card-${r.id}`}
                  className="w-full text-left p-3 min-h-[44px] active:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-500 rounded-t-lg"
                >
                  {/* Row 1 — Staff (priority 1) + chevron */}
                  <div className="flex items-center gap-2">
                    <div
                      className="font-semibold text-slate-900 truncate flex-1"
                      data-testid={`mobile-training-staff-${r.id}`}
                    >
                      {r.resolved_staff_name || "—"}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </div>

                  {/* Row 2 — Course (priority 2) */}
                  <div
                    className="text-sm text-slate-800 mt-0.5 truncate"
                    data-testid={`mobile-training-course-${r.id}`}
                  >
                    {r.resolved_course_name || "—"}
                  </div>

                  {/* Row 3 — Category (priority 3) */}
                  <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Category</span>
                    <span data-testid={`mobile-training-category-${r.id}`}>{humanise(r.resolved_category)}</span>
                  </div>

                  {/* Row 4 — Completion (priority 4a) */}
                  <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Completed</span>
                    <span className="tabular-nums" data-testid={`mobile-training-completed-${r.id}`}>
                      {fmtDate(r.completed_date)}
                    </span>
                  </div>

                  {/* Row 5 — Expiry (priority 4b) — visually emphasised when expiring/expired */}
                  <div
                    className={`text-[11px] mt-0.5 flex items-center gap-1 ${
                      status === "expired" ? "text-red-700 font-semibold"
                        : status === "expiring" ? "text-amber-800 font-semibold"
                        : "text-slate-600"
                    }`}
                    data-testid={`mobile-training-expiry-${r.id}`}
                  >
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Expiry</span>
                    <span className="tabular-nums">{expiryLine}</span>
                  </div>

                  {/* Row 6 — Compliance status (priority 5) + Certificate marker */}
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <StatusPill status={status} testid={`mobile-training-status-${r.id}`} />
                    {r.has_certificate && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] text-slate-600 border border-slate-200 bg-slate-50 rounded-full px-1.5 py-0.5"
                        data-testid={`mobile-training-cert-${r.id}`}
                      >
                        <FileText className="w-3 h-3" /> {r.certificate_id || "on file"}
                      </span>
                    )}
                  </div>
                </button>

                {/* Manager action row — Edit / Delete. Never rendered for care workers. */}
                {canManage && (
                  <div
                    className="border-t border-slate-100 px-2 py-1 flex items-center gap-1"
                    data-testid={`mobile-training-actions-${r.id}`}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditRow(r); }}
                      data-testid={`btn-mobile-edit-training-${r.id}`}
                      className="min-h-[44px] flex-1 inline-flex items-center justify-center gap-1 rounded-md text-slate-700 text-xs font-medium active:bg-slate-100"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); del(r.id); }}
                      data-testid={`btn-mobile-delete-training-${r.id}`}
                      className="min-h-[44px] w-11 inline-flex items-center justify-center rounded-md text-red-600 active:bg-red-50"
                      aria-label="Delete training record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Edit dialog — inherits Batch 1 global mobile-safe dialog patch */}
      <TrainingRecordDialog
        open={!!editRow}
        record={editRow}
        onOpenChange={(v) => { if (!v) setEditRow(null); }}
        onSaved={() => { setEditRow(null); onChanged?.(); load(); }}
      />
    </div>
  );
}

function FS({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </label>
  );
}
